import {Component, inject} from '@angular/core';
import {BaseModalComponent} from "../base-modal/base-modal.component";
import {
  advertisementPackagesResourceType,
  deleteConfirmationParams
} from "./configs/advertisement-packages-modal.config";
import {DialogAdvertisementPackagesData} from "./types/advertisement-packages-modal.types";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {CommonModule} from "@angular/common";
import {RecordData} from "../../../types/records-table.types";
import {AttachProps, DetachProps} from "../../../types/attach-detach.types";
import {BaseConfirmModalComponent} from "../base-confirm-modal/base-confirm-modal.component";
import {WrapperElementSizeStateService} from "../../../services/table-services/wrapper-element-size-state.service";
import {ColumnParamsStateService} from "../../../services/table-services/column-params-state.service";
import {
  RecordsAdvertisementPackagesComponent
} from "./components/records-advertisement-packages/records-advertisement-packages.component";
import {
  NewAdvertisementPackagesComponent
} from "./components/new-advertisement-packages/new-advertisement-packages.component";
import {LoaderComponent} from "../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-advertisement-packages-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    CommonModule,
    RecordsAdvertisementPackagesComponent,
    NewAdvertisementPackagesComponent,
    LoaderComponent
  ],
  templateUrl: './advertisement-packages-modal.component.html',
  styleUrl: './advertisement-packages-modal.component.scss'
})
export class AdvertisementPackagesModalComponent extends BaseModalComponent {
  private dialog: MatDialog = inject(MatDialog);

  public dialogData = inject<DialogAdvertisementPackagesData>(MAT_DIALOG_DATA);
  private wrapperElementSizeStateService: WrapperElementSizeStateService = inject(WrapperElementSizeStateService);
  private columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);

  public isAdvertisementPackage: boolean = false;
  public advertisementPackageId: number = 0;
  public recordCalendarId: number = 0;

  public override async ngOnInit() {
    super.ngOnInit();
    this.modalStatesService.openModal(advertisementPackagesResourceType.path);
    this.recordCalendarId = Number(this.dialogData.recordData.id);
    this.isAdvertisementPackage = !!this.dialogData.recordData['advertisementPackage'];
    if (this.isAdvertisementPackage) {
      this.advertisementPackageId = Number((this.dialogData.recordData['advertisementPackage'] as RecordData).id);
    }
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(advertisementPackagesResourceType.path);
    this.wrapperElementSizeStateService.resetSpecificWrapperElementSize(advertisementPackagesResourceType.path);
    this.columnParamsStateService.resetTableColumnParamsState(advertisementPackagesResourceType.path);
  }

  public getModalTitle(): string {
    const displayId = this.advertisementPackageId ?
      `id: ${this.advertisementPackageId}` :
      '';
    return `${advertisementPackagesResourceType.name} ${displayId}`;
  }

  public async handleAttachData(attachProps: AttachProps): Promise<void> {
    await this.attachRecordData(attachProps)
      .then(() => this.updateAdvertisementPackage(attachProps.id))
      .catch(() => this.closeDialog('cancel'));
  }

  public async handleDetachData(detachProps: DetachProps): Promise<void> {
    this.dialog.open(BaseConfirmModalComponent, {
      data: {
        deleteConfirmationParams: deleteConfirmationParams
      }
    }).afterClosed().subscribe((result) => {
      result === 'remove' && this.unbindPackage(detachProps);
    });
  }

  public handleSetLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private updateAdvertisementPackage(id: number | null): void {
    this.isAdvertisementPackage = !!id;
    this.advertisementPackageId = id ?? 0;
    this.dialogData.recordData['advertisementPackage'] = id  ?
      { id: id, valid: true } :
      null;
  }

  private async unbindPackage(detachProps: DetachProps): Promise<void> {
    await this.detachRecordData(detachProps)
      .then(() => this.updateAdvertisementPackage(null))
      .catch(() => this.closeDialog('cancel'));
  }
}
